import { A } from '@solidjs/router';
import { Show } from 'solid-js';
import complainceIconEqualHousing from '~/assets/compliance/icon-equal-housing.png';
import complainceIconMLS from '~/assets/compliance/icon-mls.png';
import complainceIconRealtor from '~/assets/compliance/icon-realtor.png';
import {
  FOLLOW_US,
  ALL_RIGHTS_RESERVED,
  PHONE,
  EMAIL,
  PRIVACY_POLICY,
  EQUAL_HOUSING_OPPORTUNITY,
  TERMS_OF_SERVICE,
} from '~/assets/strings';
import { IconFaceBook, IconInstagram, IconTK, IconTwitter, IconYoutube, IconLinkedin, IconPhone, IconMail } from '~/components/ui/Icons';
import { useLocalization } from '~/hooks/useLocalization';
import { PresentableCompayStyle } from '~/presenters/CompanyStylePresenter';

interface FooterProps {
  companyStyling: PresentableCompayStyle | undefined;
}

export const Footer = (props: FooterProps) => {
  const { t } = useLocalization();
  return (
    <div class="mt-[60px] flex w-full flex-col items-center justify-center bg-[#2C2C32] sm:mt-[90px] md:mt-[118px]">
      <div class="flex w-full max-w-container flex-col px-4 xl:px-0">
        <div class="flex w-full flex-col gap-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex w-full flex-col gap-6 border-b border-white/20 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex items-center">
              <A href="/">
                <Show
                  when={props?.companyStyling?.logo}
                  fallback={<div class="w-fit max-w-[200px] text-lg font-bold text-white">{props?.companyStyling?.name}</div>}>
                  <img src={props?.companyStyling?.logo} alt="logo" class="h-[40px] sm:h-[50px]" />
                </Show>
              </A>
            </div>

            <Show when={props?.companyStyling?.socialMedia}>
              <div class="flex items-center gap-4 text-white sm:gap-[20px]">
                <div class="text-sm">{t(FOLLOW_US)}:</div>
                <div class="flex flex-wrap items-center gap-1 sm:gap-2">
                  <Show when={props?.companyStyling?.facebook}>
                    <A href={props?.companyStyling?.facebook ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconFaceBook class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>

                  <Show when={props?.companyStyling?.instagram}>
                    <A href={props?.companyStyling?.instagram ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconInstagram class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>

                  <Show when={props?.companyStyling?.linkedIn}>
                    <A href={props?.companyStyling?.linkedIn ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconLinkedin class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>

                  <Show when={props?.companyStyling?.twitter}>
                    <A href={props?.companyStyling?.twitter ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconTwitter class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>

                  <Show when={props?.companyStyling?.youtube}>
                    <A href={props?.companyStyling?.youtube ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconYoutube class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>

                  <Show when={props?.companyStyling?.tikTok}>
                    <A href={props?.companyStyling?.tikTok ?? ''} target="_blank" class="rounded-md p-1.5 hover:bg-hover-color sm:p-2">
                      <IconTK class="size-[12px] sm:size-[14px]" color="white" />
                    </A>
                  </Show>
                </div>
              </div>
            </Show>
          </div>
        </div>

        <div class="flex flex-col gap-6 pb-6 md:flex-row md:items-start md:justify-between">
          <div class="flex flex-wrap gap-4">
            <Show when={props?.companyStyling?.complianceIconRealtor || true}>
              <img class="size-12 object-contain sm:size-14" src={complainceIconRealtor} alt="Realtor" />
            </Show>
            <Show when={props?.companyStyling?.complianceIconMls || true}>
              <img class="size-12 object-contain sm:size-14" src={complainceIconMLS} alt="Master Listing Service" />
            </Show>
            <Show when={props?.companyStyling?.equalHouseing || true}>
              <img class="size-12 object-contain sm:size-14" src={complainceIconEqualHousing} alt="Equal Housing Opportunity" />
            </Show>
          </div>

          <div class="flex max-w-md flex-col gap-3">
            <Show when={props?.companyStyling?.email}>
              <div class="flex items-center gap-3 rounded-lg bg-[#383840] p-3">
                <div class="flex items-center border-r border-white/20 pr-3">
                  <IconMail class="size-5 text-white/40" />
                </div>
                <div class="flex flex-col">
                  <span class="text-sm text-white/40">{EMAIL}</span>
                  <a
                    href={`mailto:${props?.companyStyling?.email}`}
                    class="break-all text-sm text-white hover:text-hover-color hover:underline sm:break-normal">
                    {props?.companyStyling?.email}
                  </a>
                </div>
              </div>
            </Show>

            <Show when={props?.companyStyling?.phoneNumber}>
              <div class="flex items-center gap-3 rounded-lg bg-[#383840] p-3">
                <div class="flex items-center border-r border-white/20 pr-3">
                  <IconPhone class="size-5 text-white/40" />
                </div>
                <div class="flex flex-col">
                  <span class="text-sm text-white/40">{PHONE}</span>
                  <a href={`tel:${props?.companyStyling?.phoneNumber}`} class="text-sm text-white hover:text-hover-color hover:underline">
                    {props?.companyStyling?.phoneNumber}
                  </a>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </div>

      <div class="w-full bg-[#24242B] py-4 pb-20">
        <div class="mx-auto max-w-container px-4 xl:px-0">
          <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div class="text-sm text-white">
              {ALL_RIGHTS_RESERVED} - {props?.companyStyling?.name}
            </div>

            <div class="flex flex-wrap gap-4 text-sm text-white/60">
              <A href="/privacy-policy" class="underline hover:text-hover-color">
                {PRIVACY_POLICY}
              </A>
              <span class="text-white/20">|</span>
              <A
                href="https://portal.magicdoor.com/terms-of-service.pdf"
                class="underline hover:text-hover-color"
                target="_blank"
                rel="noopener noreferrer">
                {TERMS_OF_SERVICE}
              </A>
              <Show when={props?.companyStyling?.equalHouseing || true}>
                <span class="text-white/20">|</span>
                <A
                  href="https://www.hud.gov/fairhousing"
                  class="underline hover:text-hover-color"
                  target="_blank"
                  rel="noopener noreferrer">
                  {EQUAL_HOUSING_OPPORTUNITY}
                </A>
              </Show>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
